import { EventType } from "@/analytics/analytics-events";
import { OpenRegistrationProperties } from "@/analytics/staging-area-workflows-events-properties";
import { AppHeaderBar } from "@/components/common/app-header-bar";
import { ElementFileUploadContextProvider } from "@/components/common/point-cloud-file-upload-context/element-file-upload-context";
import { useUpdateProjectMetadata } from "@/components/common/project-provider/use-project-metadata";
import { useTrackProjectViewedAuditEvent } from "@/components/common/project-provider/use-track-project-events";
import { useDataManagementContext } from "@/components/ui/data-management/data-management-context";
import { DataManagementEditor } from "@/components/ui/data-management/data-management-editor";
import { PublishSuccessDialog } from "@/components/ui/data-management/publish-success-dialog";
import { RegisterSuccessDialog } from "@/components/ui/data-management/register-success-dialog";
import { ModalSpinner } from "@/components/ui/modal-spinner";
import { FeatureTourManager } from "@/features/feature-tour/feature-tour-manager";
import { THRESHOLD_SET_STATIONARY } from "@/registration-tools/common/registration-report/registration-thresholds";
import { ThresholdSetProvider } from "@/registration-tools/common/registration-report/threshold-set-context";
import { ViewerTool } from "@/store/features/features";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { selectShowSpinner } from "@/store/ui/ui-selectors";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import {
  appStateSelector,
  DevToolsForDraftView,
  useFetchDataForEditor,
  ViewMode,
} from "@faro-lotv/data-management";
import { grey, LoadingScreen } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID } from "@faro-lotv/foundation";
import {
  RegisteredUserInfo,
  RegistrationRevision,
  RegistrationStatus,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Stack } from "@mui/material";
import { useState, type JSX } from "react";
import {
  DataPreparationOverlayElementsContext,
  useDataPreparationOverlayElementsInitialState,
} from "./data-preparation-overlay-context";
import { EditConnectionContextProvider } from "./edit-connection-context";
import { RevisionScansScene } from "./rendering/revision-scans-scene";
import { selectValidRevisionScans } from "./store/data-preparation-ui/data-preparation-ui-selectors";
import { selectRevisionRegistrationEdges } from "./store/revision/revision-edge-selectors";
import {
  selectRevisionEntities,
  selectRootEntity,
} from "./store/revision/revision-entity-selectors";
import {
  selectIsAnyEditModeEnabled,
  selectIsEditingScans,
} from "./store/user-edits/data-preparation-user-edit-selectors";
import { DataPreparationLoadingSpinner } from "./ui/data-preparation-loading-spinner";
import { DataPreparationCanvasOverlay } from "./ui/data-preparation-overlay";
import { DataPreparationReportSidebar } from "./ui/data-preparation-report-sidebar";
import { DataPreparationSidebar } from "./ui/data-preparation-sidebar";
import { DraftImportDataDialog } from "./ui/draft-import-data-dialog";
import { ReportProvider } from "./ui/report-context";
import { isScanEntityUsable } from "./utils/entity-utils";

type DataPreparationUiProps = {
  /** The revision that is used for the data preparation. */
  revision?: RegistrationRevision;
  /** Currently logged-in user, needed by the Data Management editor. */
  currentUser: RegisteredUserInfo;
  /** Project ID, needed by the Data Management editor. */
  projectId: GUID;
};

/**
 * @returns UI for the data preparation page.
 */
export function DataPreparationUi({
  revision,
  currentUser,
  projectId,
}: DataPreparationUiProps): JSX.Element {
  const { projectApiClient } = useApiClientContext();

  // It's only possible to decide whether to show the Inspect UI or the Data Management editor
  // after the Data Management editor has fetched its initial data and determined its view mode.
  // While the Data Management editor would trigger the fetching itself, it's necessary to be
  // informed of the fetch state here to show the loading spinner while fetching is in progress.
  const { isFetching } = useFetchDataForEditor({
    projectId,
    currentUser,
  });
  const { isDevModeEnabled } = useAppSelector(appStateSelector);

  // It's necessary to get informed by the Data Management editor of the view mode it calculated.
  const [viewMode, setViewMode] = useState<ViewMode>("INITIAL");

  // Dev-only: hide WIZARD/ERROR temporarily to inspect poses without cancelling progress. Shared
  // between the Data Management editor (which computes the view mode) and DevToolsForDraftView (which provides a toggle to control it).
  const [isInspectForced, setIsInspectForced] = useState(false);

  // showSpinner is set to true when a file check by the DataManagementPicker is carried out.
  // Using the LoadingScreen looks weird in this case. Instead, a ModalSpinner is used just like
  // in the ImportDataDialog.
  const showSpinner = useAppSelector(selectShowSpinner);

  const store = useAppStore();

  // Non-blocking: Load project meta-data, e.g. to get the project name for the breadcrumbs
  useUpdateProjectMetadata(projectApiClient.projectId);

  const isEditModeEnabled = useAppSelector(selectIsAnyEditModeEnabled);
  const isEditingScans = useAppSelector(selectIsEditingScans);

  const revisionEntities = useAppSelector(selectRevisionEntities);
  const revisionEdges = useAppSelector(selectRevisionRegistrationEdges);
  const { setScanFileInfos } = useDataManagementContext();

  useNonExhaustiveEffect(() => {
    const state = store.getState();
    const scanEntities = selectValidRevisionScans(state);
    const rootEntity = selectRootEntity(state);
    const noPotreeScans = scanEntities.filter(
      (entity) => !isScanEntityUsable(entity),
    );
    Analytics.track<OpenRegistrationProperties>(EventType.openRegistration, {
      registrationStatus:
        rootEntity?.registrationStatus ?? RegistrationStatus.unknown,
      noPotreeScansCount: noPotreeScans.length,
    });
    // no deps, only trigger on initial mount
  }, []);

  const dataPreparationOverlayElements =
    useDataPreparationOverlayElementsInitialState();

  useTrackProjectViewedAuditEvent(projectId, isFetching);

  // Also show a loading screen while the Data Management editor determines its view mode.
  // However, to be able to do that, it needs to be included in the component tree. This means a
  // new instance of the DataManagementEditor will get mounted for the main branch once it
  // determined the view mode. Using a variable for it would not solve this issue. However, since
  // it happens only once at the end of the loading phase, the impact should be minimal. A proper
  // fix by introducing a useViewMode hook would cause some implementation effort.
  if (isFetching || viewMode === "INITIAL") {
    return (
      <>
        <LoadingScreen />
        {!isFetching && (
          <DataManagementEditor viewMode={viewMode} setViewMode={setViewMode} />
        )}
      </>
    );
  }

  return (
    <Stack
      sx={{
        height: "100%",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <ModalSpinner
        sx={{
          color: grey[0],
          // Set the z-index to achieve the following stacking of UI elements:
          //  1) Toasts: snackbar = 1400
          //  2) MainUi > ModalSpinner: snackbar - 1 = 1399
          //  3) Data Management > DataCategoryDialog: snackbar - 2 = 1398
          //  4) ImportDataDialog > Dialog: snackbar - 3 = 1397
          //  5) NewFeaturePopup > FaroPopover: modal + 1 = 1301
          zIndex: (theme) => theme.zIndex.snackbar - 1,
        }}
        open={showSpinner}
      />

      {/* Only show the feature tour when the Inspect UI is visible, not the pages provided by
          Data Management. */}
      {viewMode === "INSPECT" && (
        <FeatureTourManager tool={ViewerTool.dataPreparationTool} />
      )}

      <EditConnectionContextProvider>
        <DataPreparationOverlayElementsContext
          value={dataPreparationOverlayElements}
        >
          <AppHeaderBar tool={ViewerTool.dataPreparationTool} />

          <ThresholdSetProvider
            defaultThresholdSet={THRESHOLD_SET_STATIONARY}
            showThresholdSetSwitch={false}
          >
            <Stack sx={{ flexGrow: 1, width: "100%", overflow: "hidden" }}>
              <RegisterSuccessDialog />
              <PublishSuccessDialog />

              {/* Rendered here instead of next to the button opening it in the sidebar, so that
                  it stays open when the sidebar gets unmounted when reducing the screen size. */}
              <ElementFileUploadContextProvider>
                <DraftImportDataDialog />
              </ElementFileUploadContextProvider>

              <DataManagementEditor
                viewMode={viewMode}
                setViewMode={setViewMode}
                isInspectForced={isInspectForced}
                setIsInspectForced={setIsInspectForced}
              />

              {isDevModeEnabled && (
                <DevToolsForDraftView
                  viewMode={viewMode}
                  setFileInfos={setScanFileInfos}
                  revision={revision}
                  entities={revisionEntities}
                  edges={revisionEdges}
                  isInspectForced={isInspectForced}
                  setIsInspectForced={setIsInspectForced}
                />
              )}

              {viewMode === "INSPECT" && (
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    flexGrow: 1,
                    width: "100%",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <DataPreparationSidebar />
                  <ReportProvider>
                    {revision && (
                      <RevisionScansScene
                        registrationState={revision.state}
                        overlay={
                          <>
                            <DataPreparationLoadingSpinner
                              revisionState={revision.state}
                            />
                            <DataPreparationCanvasOverlay
                              revision={revision}
                              isEditRegistrationEnabled={isEditModeEnabled}
                            />
                          </>
                        }
                        isEditingScans={isEditingScans}
                        isEditRegistrationEnabled={isEditModeEnabled}
                      />
                    )}
                    <DataPreparationReportSidebar />
                  </ReportProvider>
                </Stack>
              )}
            </Stack>
          </ThresholdSetProvider>
        </DataPreparationOverlayElementsContext>
      </EditConnectionContextProvider>
    </Stack>
  );
}
